"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Store, Building2, Wrench, CircleCheck as CheckCircle2, ArrowRight, Calendar, Compass, FileCheck, PhoneCall } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
const sectorGuidance = [{
  sector: "Trades & home services",
  iconKey: "wrench",
  summary: "For plumbers, electricians, roofers, and builders where emergency intent and verified local proximity drive high-ticket enquiries.",
  focus: "Emergency dispatch queries, strict service area configuration, and geo-tagged job-site proof."
}, {
  sector: "Hospitality & dining",
  iconKey: "store",
  summary: "For independent restaurants, boutique hotels, and venues requiring high footfall and immediate table or room reservations.",
  focus: "Real-time menu attributes, direct booking engine links, and recurring photo velocity on Maps."
}, {
  sector: "Property & estate agents",
  iconKey: "building",
  summary: "For letting agents, surveyors, and estate agencies needing trusted visibility across distinct postcodes and neighborhoods.",
  focus: "Hyper-local neighborhood hubs, localized valuation intent, and regional directory authority."
}];
const pillars = [{
  number: "01",
  title: "Google Business Profile precision",
  description: "I personally rebuild your primary category mapping, secondary attributes, service lists, and photo schedules so Google trusts your physical proximity and operating capacity.",
  deliverable: "Complete GBP audit, category hierarchy repair, and weekly optimization workflow."
}, {
  number: "02",
  title: "Clean local citation architecture",
  description: "Inconsistent NAP data (Name, Address, Phone) silently damages map rankings. I curate, clean, and synchronize genuine regional directories, industry associations, and local business listings.",
  deliverable: "Manual citation cleanup, duplicate removal, and structured UK aggregator sync."
}, {
  number: "03",
  title: "Location-specific landing pages",
  description: "Rather than creating thin doorway pages that search engines penalize, I develop rich, helpful location pages with genuine customer FAQs, service specifics, and schema markup.",
  deliverable: "Unique area service pages, LocalBusiness JSON-LD markup, and localized internal links."
}, {
  number: "04",
  title: "Search-to-enquiry conversion paths",
  description: "Ranking in the local map pack is only half the job. I streamline your mobile tap-to-call buttons, enquiry forms, and review collection systems so visitors become confirmed customers.",
  deliverable: "Conversion audits, click-to-call event tracking, and straightforward booking friction removal."
}];
export default function LocalGuidance() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="localseostrategyguidance" data-nav="light" className="relative bg-background py-20 md:py-32 overflow-hidden border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="max-w-3xl mb-16 md:mb-24">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-medium mb-6">
            <Compass className="size-3.5 text-accent" />
            <span>Consultant Strategic Guide</span>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-default font-bold text-foreground tracking-tight leading-tight mb-6">
            Local search strategy that brings customers through your door.
          </h2>

          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            I provide practical local SEO guidance for UK trades and hospitality businesses, turning map searches into direct phone enquiries, table reservations, and booked call-outs. When you hire me, you get my direct strategic attention from day one.
          </p>
        </motion.div>

        {/* Tailored Sectors Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20 md:mb-28">
          {sectorGuidance.map((item, i) => <motion.div key={item.sector} data-index={i} variants={itemVariants}>
              <Card className="h-full border border-border bg-card/60 rounded-xl hover:border-primary/40 shadow-xs transition-[border-color,box-shadow]">
                <CardHeader className="p-6 pb-4">
                  <div className="size-12 rounded-lg bg-secondary flex items-center justify-center mb-4">
                    {item.iconKey === "wrench" && <Wrench className="size-6 text-primary" />}
                    {item.iconKey === "store" && <Store className="size-6 text-primary" />}
                    {item.iconKey === "building" && <Building2 className="size-6 text-primary" />}
                  </div>
                  <CardTitle className="text-xl font-default font-semibold text-foreground">
                    {item.sector}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 pt-0 space-y-4">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.summary}
                  </CardDescription>
                  <div className="pt-3 border-t border-border/80">
                    <p className="text-xs font-medium text-foreground uppercase tracking-wider mb-1.5">
                      My strategic focus
                    </p>
                    <p className="text-xs text-muted-foreground leading-normal">
                      {item.focus}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Core Strategy Pillars */}
        <div className="mb-20 md:mb-28">
          <div className="max-w-2xl mb-12">
            <Badge variant="outline" className="mb-3 text-xs">
              Direct Implementation
            </Badge>
            <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground">
              Four fundamental pillars of every local engagement
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base mt-3">
              I do not run automated scripts and pass off generic reports. I work directly on the four areas that actually produce calls and bookings.
            </p>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {pillars.map((pillar, i) => <motion.div key={pillar.number} data-index={i} variants={itemVariants} className="flex flex-col justify-between p-8 rounded-xl border border-border bg-card shadow-xs">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-mono font-bold text-accent px-2.5 py-1 rounded bg-secondary">
                      {pillar.number}
                    </span>
                    <FileCheck className="size-5 text-primary/70" />
                  </div>
                  <h4 className="text-xl font-default font-semibold text-foreground mb-3">
                    {pillar.title}
                  </h4>
                  <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-6">
                    {pillar.description}
                  </p>
                </div>
                <div className="pt-4 border-t border-border flex items-start gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <p className="text-xs text-foreground font-medium">
                    <span className="text-muted-foreground">Core outcome: </span>
                    {pillar.deliverable}
                  </p>
                </div>
              </motion.div>)}
          </motion.div>
        </div>

        {/* Personal Expert Callout & Direct Next Step */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="rounded-2xl border border-border bg-muted/40 p-8 sm:p-10 md:p-12 relative overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8 space-y-4">
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wider">
                <MapPin className="size-4 text-accent" />
                <span>One-to-one consultation</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground">
                Want me to review your local search visibility?
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed max-w-2xl">
                I will assess your current Google Business Profile, spot citation inconsistencies, inspect your local pages, and outline exactly what is stopping local buyers from reaching you.
              </p>
            </div>

            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 justify-start lg:justify-end">
              <Button asChild size="lg" className="w-full justify-center">
                <Link to="/ContactPage">
                  <SafeHydrate><Calendar className="size-4 mr-2" /></SafeHydrate>
                  Book a free SEO consultation
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full justify-center">
                <Link to="/LocalSEOPage">
                  <span>Explore Local SEO service</span>
                  <ArrowRight className="size-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-border/80 flex flex-wrap items-center justify-between gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <PhoneCall className="size-3.5 text-primary" />
              <span>Direct access: +44 7490984430</span>
            </div>
            <div>
              <span>Direct local trades and hospitality guidance with Yassine Essaadi, never account managers.</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}